/**
 * Quick Mark Won Modal
 * 
 * Micro-modal with 1 question: "What worked?"
 * 2 seconds, done.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceResolver } from '@/hooks/useWorkspaceResolver';
import { useAuth } from '@/lib/auth';
import { Deal } from '@/hooks/useDeals';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { errorMessage } from '@/lib/errors';

const WIN_REASONS = [
  { value: 'time', label: 'Time / Urgency' },
  { value: 'price', label: 'Price / Value' },
  { value: 'relationship', label: 'Relationship / Trust' },
  { value: 'follow_up', label: 'Follow-up / Persistence' },
  { value: 'demo', label: 'Demo / Presentation' },
  { value: 'other', label: 'Other' },
];

interface QuickMarkWonModalProps {
  deal: Deal;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuickMarkWonModal({ deal, open, onOpenChange }: QuickMarkWonModalProps) {
  const { workspaceId } = useWorkspaceResolver();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [winReason, setWinReason] = useState('');
  const [finalValue, setFinalValue] = useState(deal.value?.toString() || '');

  const markWonMutation = useMutation({
    mutationFn: async () => {
      const value = finalValue ? parseFloat(finalValue) : (deal.value || 0);

      // Update deal
      const { data, error } = await supabase
        .from('deals')
        .update({ 
          stage: 'won',
          deal_status: 'won',
          value: value,
          closed_at: new Date().toISOString(),
          won_at: new Date().toISOString(),
          win_reason: winReason || 'other', // Store what worked
        } as any)
        .eq('id', deal.id as any)
        .select('id, lead_id')
        .single();

      if (error) throw toError(error, 'Failed to mark deal as won');
      
      const leadId = (data as any)?.lead_id;

      // Update lead pipeline_stage to 'won' if lead exists
      if (leadId) {
        await supabase
          .from('leads')
          .update({ pipeline_stage: 'won' } as any)
          .eq('id', leadId);
      }

      // Log the action event
      if (workspaceId && user?.id && leadId) {
        await supabase.from('action_events').insert({
          workspace_id: workspaceId,
          user_id: user.id,
          lead_id: leadId,
          action_type: 'stage_change',
          outcome: 'closed_won',
          meta: { deal_id: deal.id, new_stage: 'won', final_value: value, win_reason: winReason },
        } as any);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['revenue-analytics', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['leads', workspaceId] });
      toast.success('🎉 Deal Won!', {
        description: `Congratulations! You've closed ${deal.lead?.name || 'this deal'}.`
      });
      setWinReason('');
      onOpenChange(false);
    },
    onError: (error: any) => {
      console.error('MARK_WON_ERROR', error);
      toast.error('Error', { description: errorMessage(error, 'Failed to mark deal as won') });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!winReason) {
      toast.error('Please select what worked');
      return;
    }
    markWonMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Trophy className="w-5 h-5 text-success" />
            What worked?
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-success/10 border border-success/20 rounded-lg">
            <p className="font-medium text-sm">{deal.lead?.name || 'Deal'}</p>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">Final Deal Value ({deal.currency || 'EUR'})</label>
            <input
              type="number"
              value={finalValue}
              onChange={(e) => setFinalValue(e.target.value)}
              placeholder="Enter final value"
              min="0"
              step="0.01"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            {deal.value && finalValue && parseFloat(finalValue) !== deal.value && (
              <p className="text-xs text-muted-foreground">
                Original: {deal.value.toLocaleString()} {deal.currency}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">What worked?</label>
            <div className="grid grid-cols-2 gap-2">
              {WIN_REASONS.map(reason => (
                <Button
                  key={reason.value}
                  type="button"
                  variant={winReason === reason.value ? 'default' : 'outline'}
                  onClick={() => setWinReason(reason.value)}
                  className="h-10 text-sm"
                >
                  {reason.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} size="sm">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!winReason || markWonMutation.isPending}
              className="bg-success hover:bg-success/90 text-success-foreground"
              size="sm"
            >
              {markWonMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Trophy className="w-4 h-4 mr-2" />
              Mark Won
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

